// Modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import get from 'lodash/get';
import ReactCSSTransitionGroup from '../ReactCSSTransitionGroupCompat/ReactCSSTransitionGroupCompat';

// Components
import OMRadioButtonItem from '../OMComponents/OMRadioButtonItem/OMRadioButtonItem';
import OMTooltip from '../OMComponents/OMTooltip/OMTooltip';
import HighlightTitleForPublisherPackage from '../HighlightTitleForPublisherPackage/HighlightTitleForPublisherPackage';
import RealtorPackageDetails from './Details/RealtorPackageDetails';
import PublisherPackageDetails from './Details/PublisherPackageDetails';
// Helpers
import { packageIcons } from '../../constants';
import clearNumber from '../../helpers/clearNumber';
import beautifyNumber from '../../helpers/beautifyNumber';
import { calculatePriceWithDiscount } from '../../helpers/paymentCalculation';
import { packageTypes } from '../../packagesData';
// Styles
import * as styles from './PackageItem.scss';
import promoImages from '../../../static/icons/realtorLanding';

export default class PackageItem extends Component {
  getPromoImage = (highlight, topPromotion, autojump) => {
    if (highlight && autojump) {
      return promoImages.highlight;
    }

    if (highlight) {
      return promoImages.highlight;
    }

    if (autojump) {
      return promoImages.autojump;
    }

    if (topPromotion) {
      return promoImages.top;
    }

    return '';
  };

  renderPromoImage = (image) => {
    if (!image) {
      return null;
    }

    if (typeof image === 'string') {
      return <img src={image} alt="" loading="lazy" decoding="async" />;
    }

    return (
      <picture>
        <source srcSet={image.webp} type="image/webp" />
        <img src={image.fallback} alt="" loading="lazy" decoding="async" />
      </picture>
    );
  };

  renderDiscount = discount => (
    <div className={cx(styles.discount, discount > 10 ? styles.pink : styles.yellow)}>
      {`${discount}% ${this.props.translate('discount')}`}
    </div>
  );

  renderRealtorPackageDetails = () => (
    <RealtorPackageDetails
      translate={this.props.translate}
      isMobile={this.props.isMobile}
      locale={this.props.locale}
      packageData={this.props.package}
      selectedPackage={this.props.selectedPackage}
      changeSelectedPayment={this.props.changeSelectedPayment}
    />
  );

  renderPublisherPackageDetails = () => {
    const type = get(this.props, 'package.type');
    const selectedPeriod = get(this.props, 'selectedPackage.period');
    const alternativePeriod = get(this.props, 'selectedPackage.alternativePeriod');
    const currentPeriod = get(this.props, `package.period.${selectedPeriod}`) ? selectedPeriod : alternativePeriod;
    const isRegularAd = (type === packageTypes.REGULAR);
    const isTopAd = (type === packageTypes.TOP);

    return !isRegularAd && (
      <PublisherPackageDetails
        translate={this.props.translate}
        packageType={type}
        selectedPeriod={currentPeriod}
        requiredMessage={isTopAd && this.props.translate('topPromotionRequiredMessage')}
      />
    );
  };

  renderDetails = () => ((this.props.isAgent)
    ? this.renderRealtorPackageDetails()
    : this.renderPublisherPackageDetails());

  renderTooltipMsg = (withHighlight, isTopPromotion, withAutojump) => ((withHighlight || isTopPromotion || withAutojump)
    ? (
      <div className={styles.tooltipContent}>
        {this.renderPromoImage(this.getPromoImage(withHighlight, isTopPromotion, withAutojump))}
        { /*
        <div className={styles.tooltipTitle}>{this.props.translate((isTopPromotion) ? 'topPromotionTooltipTitle' : 'tooltipTitle')}</div>
        <i className="icon-star">
          <div className={styles.starTitle}>
            <span className={styles.quantity}>{(isTopPromotion) ? 'x10' : 'x3'}</span>
            <span className={styles.calls}>{this.props.translate('calls')}</span>
          </div>
        </i>
        */ }
      </div>
    ) : (
      <div className={styles.tooltipContent}>
        <img className={styles.withoutHighlight} src={packageIcons.withoutHighlight} alt="" />
      </div>
    ));

  renderPrice = (price) => {
    const beautifiedPrice = beautifyNumber(clearNumber(price));
    const separator = (this.props.isAgent) ? ' ' : ' / ';
    const monthKey = (this.props.isAgent) ? 'perMonth' : 'month';
    const perMonth = this.props.isMobile && !this.props.isAgent
      ? ''
      : (
        <span className={cx({ [styles.perMonth]: !this.props.isAgent })}>
          {separator}{this.props.translate(monthKey)}
        </span>
      );

    const realtorPackagePrice = <span className={styles.priceTitle}>{`₪${beautifiedPrice}`}{perMonth}</span>;

    const publisherPackagePrice = (beautifiedPrice === '0')
      ? this.props.translate('freePrice')
      : <span>{`₪${beautifiedPrice}`}</span>;

    return (this.props.isAgent) ? realtorPackagePrice : publisherPackagePrice;
  };

  renderRealtorTitle = () => {
    const { package: { properties } } = this.props;
    return {
      properties: <span className={styles.boldText}>{`${this.props.translate('plan')} ${properties}`}</span>
    };
  };

  renderPublisherTitle = () => {
    const { package: { period, type }, selectedPackage } = this.props;
    const { highlight, autojump, top_promotion } = (
      period[selectedPackage.period]
      || period[selectedPackage.alternativePeriod]
      || {}
    );

    return {
      properties: <span className={styles.boldText}>{this.props.translate(`${type}Ad`)}</span>,
      highlight: (
        <HighlightTitleForPublisherPackage
          haveHighlight={highlight}
          haveAutojump={autojump}
          haveTopPromotion={top_promotion}
          topPromotionLabel={`${this.props.translate('topPromotionLabel')}`}
        />
      )
    };
  };

  renderTitle = () => ((this.props.isAgent) ? this.renderRealtorTitle() : this.renderPublisherTitle());

  render() {
    const { package: { period, id }, selectedPackage, isAgent, isMobile } = this.props;
    const currentPeriod = period[selectedPackage.period] ? selectedPackage.period : selectedPackage.alternativePeriod;
    const { price, discount, highlight, top_promotion, autojump } = period[currentPeriod] || {};

    const prices = {
      month: this.renderPrice(price === 350 ? 352 : price), // really needs, principal crutch
      withDiscount: this.renderPrice(calculatePriceWithDiscount(this.props.package, currentPeriod))
    };
    const checked = selectedPackage.checked === id;

    return (
      <OMRadioButtonItem
        className={this.props.className}
        element={this.renderTitle()}
        params={{
          name: `package${id}`,
          onChange: () => this.props.changeSelectedPayment({ checked: id }),
          value: id,
          checked,
          isMobile,
          isAgent
        }}
        details={this.renderDetails()}
        parameters={!isAgent && {
          onMouseEnter: () => this.props.handleMouseEnter(id),
          onMouseLeave: () => this.props.handleMouseLeave(id)
        }}
      >
        {((isAgent && !checked) || (!isAgent)) && (
          <div className={cx(styles.pricePart, { [styles.publisherPrice]: !isAgent })}>
            <div className={styles.discountWrapper}>
              {!!discount && this.renderDiscount(discount)}

              <div className={styles.priceWithDiscount} style={this.props.isAgent ? { position: 'static' } : {}}>
                <div>{prices.withDiscount}</div>
                {!!discount && (
                  <div className={styles.clearPrice}>
                    {prices.month}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!this.props.isMobile && !isAgent && (
          <ReactCSSTransitionGroup
            transitionName="package-tooltip"
            transitionAppear
            transitionAppearTimeout={300}
            transitionEnterTimeout={300}
            transitionLeaveTimeout={300}
            onClick={(evt) => evt.preventDefault()}
          >
            {this.props.showTooltip && (
              <OMTooltip
                key={`package-tooltip-${id}`}
                msg={this.renderTooltipMsg(highlight, top_promotion, autojump)}
                className="packageTooltip"
                onClose={this.props.handleCloseTooltip}
              />
            )}
          </ReactCSSTransitionGroup>
        )}
      </OMRadioButtonItem>
    );
  }
}

PackageItem.propTypes = {
  package: PropTypes.object,
  periods: PropTypes.array,
  translate: PropTypes.func,
  locale: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  isAgent: PropTypes.bool,
  className: PropTypes.string,
  changeSelectedPayment: PropTypes.func.isRequired,
  selectedPackage: PropTypes.object.isRequired,
  hasPhotos: PropTypes.bool,
  showTooltip: PropTypes.bool,
  handleMouseEnter: PropTypes.func,
  handleMouseLeave: PropTypes.func,
  handleCloseTooltip: PropTypes.func
};
